export const getUpdatedCampaignConfiguration = (
  campaignsConfiguration,
  campaignGroups
) => {
  let temporaryCampaignsConfig = JSON.parse(JSON.stringify(campaignsConfiguration));
  const updatedConfigs = [];

  if (campaignGroups?.length && temporaryCampaignsConfig.length) {
    campaignGroups.forEach((group) => {
      const updatedGroup = {
        key: group.key,
        image: group.imageURL,
        name: group.name,
        campaigns: [],
        value: null,
      };

      if (group.campaigns?.length) {
        group.campaigns.forEach((campaignKey) => {
          const campaign = temporaryCampaignsConfig.find(
            (config) => config.key === campaignKey
          );
          if (campaign) {
            updatedGroup.campaigns.push(campaign);
            temporaryCampaignsConfig = temporaryCampaignsConfig.filter(
              (config) => config.key !== campaignKey
            );
          }
        });
      }

      updatedConfigs.push(updatedGroup);
    });
  }

  return temporaryCampaignsConfig.concat(updatedConfigs);
};

export const getCampaignKeyToTypesMap = (campaignConfiguration) => {
  const campaignKeyToTypesMap = {};
  campaignConfiguration.forEach((campaign) => {
    if (campaign.campaigns && campaign.campaigns.length > 0) {
      campaignKeyToTypesMap[campaign.key] = campaign.campaigns.map((c) => c.key);
      return;
    }
    campaignKeyToTypesMap[campaign.key] = [campaign.key];
  });
  return campaignKeyToTypesMap;
};

//sort activity rules based on the activityDefinition.rulesCOnfig.rules || activityDefinition.rules
export const sortActivityRules = (existingRules, activityDefinition) => {
  const rules = JSON.parse(JSON.stringify(existingRules));
  // Early return if activityDefinition is undefined
  if (!activityDefinition) {
    return rules;
  }

  // Get rule definitions
  const existingRuleDefs =
    activityDefinition.rulesConfiguration?.rules || activityDefinition.rules || [];
  // Create a slug-to-position mapping for faster lookups
  const slugPositionMap = {};
  // Extract all slugs in order, prioritizing the nested structure
  existingRuleDefs.forEach((ruleDef, index) => {
    // Use rule.slug if available, otherwise try direct slug
    const slug = (ruleDef.rule && ruleDef.rule.slug) || ruleDef.slug;
    if (slug) {
      slugPositionMap[slug] = index;
    }
  });
  // Sort rules based on the position map
  const sortedRules = [...rules].sort((a, b) => {
    const posA = slugPositionMap[a.ruleDefinitionSlug];
    const posB = slugPositionMap[b.ruleDefinitionSlug];
    // Sort by position, unknown rules go to the end
    return (
      (posA === undefined ? Infinity : posA) - (posB === undefined ? Infinity : posB)
    );
  });

  return sortedRules;
};

const RULE_SLUGS = {
  MATCH_PRODUCTS_KEYWORDS: "match-package-products-keywords",
  MATCH_PRODUCTS_KEYWORD_POINTS: "match-package-products-keyword-points-calculator",
  MATCH_RECEIPT_PRODUCTS: "match-receipt-products-keywords",
  MATCH_RECEIPT_PRODUCTS_POINTS: "match-receipt-products-keyword-points-calculator",
};

const OUTCOME_SLUGS = {
  POINTS_BASED_STREAKS: "points-based-streaks",
};

const IGNORED_RULE_SLUGS = [
  RULE_SLUGS.MATCH_PRODUCTS_KEYWORDS,
  RULE_SLUGS.MATCH_RECEIPT_PRODUCTS,
];
const CUSTOM_HANDLED_RULE_SLUGS = [
  RULE_SLUGS.MATCH_PRODUCTS_KEYWORD_POINTS,
  RULE_SLUGS.MATCH_RECEIPT_PRODUCTS_POINTS,
];
const ACTIVITIES_CONFIGURATION_OUTCOMES = [OUTCOME_SLUGS.POINTS_BASED_STREAKS];

export const mapCampaignActivities = (
  activities,
  campaignOutcomes,
  params,
  campaignObject
) =>
  activities.map((activity) => {
    const {
      activityDefinition,
      activityRules,
      activityData,
      outcomes,
      id,
      name,
      description,
    } = activity;

    const ruleStrategies = {
      [RULE_SLUGS.MATCH_PRODUCTS_KEYWORD_POINTS]: (rules) => {
        const keywordIds = [...new Set(rules.flatMap((rule) => rule.ruleData.ids))];
        activityPayload.activityRules.push({
          ruleDefinitionSlug: RULE_SLUGS.MATCH_PRODUCTS_KEYWORDS,
          ruleData: { ids: keywordIds },
        });
      },
      [RULE_SLUGS.MATCH_RECEIPT_PRODUCTS_POINTS]: (rules) => {
        const keywordIds = [...new Set(rules.flatMap((rule) => rule.ruleData.ids))];
        activityPayload.activityRules.push({
          ruleDefinitionSlug: RULE_SLUGS.MATCH_RECEIPT_PRODUCTS,
          ruleData: { ids: keywordIds },
        });
      },
    };

    const mapActivityRules = (rules) =>
      rules
        .filter((rule) => !IGNORED_RULE_SLUGS.includes(rule.ruleDefinition.slug))
        .map(({ ruleDefinition, ruleData }) => ({
          ruleDefinitionSlug: ruleDefinition.slug,
          ruleData,
        }));
    const mapOutcomes = (outcomes) => {
      const uniqueLeaderboardIds = new Set();

      const catalogRefs = campaignObject?.catalogRefs || [];
      const leaderboardIds = campaignObject?.leaderboards || [];
      const uniqueCatalogIds = new Set();
      if (catalogRefs?.length) {
        catalogRefs.forEach((ref) => {
          if (ref.catalogId) {
            uniqueCatalogIds.add(ref.catalogId);
          }
        });
      }
      if (leaderboardIds?.length) {
        leaderboardIds.forEach((id) => {
          uniqueLeaderboardIds.add(id);
        });
      }
      const addedCatalogIds = new Set();
      return outcomes.map((outcome) => {
        const { outcomeDefinition, outcomeData, outcomeRules } = outcome;

        // Handle leaderboard points
        if (outcomeDefinition?.slug === "award-leaderboard-points") {
          if (
            outcome.outcomeData &&
            outcome.outcomeData.rewards &&
            Array.isArray(outcome.outcomeData.rewards)
          ) {
            outcome.outcomeData.rewards.forEach((reward) => {
              addedCatalogIds.add(reward.catalog_id);
              if (reward.catalog_id && !uniqueCatalogIds.has(reward.catalog_id)) {
                uniqueCatalogIds.add(reward.catalog_id);
                catalogRefs.push({
                  catalogId: reward.catalog_id,
                  attachedAt: new Date().toISOString(),
                });
              }
            });
          }
          uniqueLeaderboardIds.add(outcome?.outcomeData?.leaderboard_id);
        }

        campaignObject.catalogRefs = catalogRefs.filter((ref) =>
          addedCatalogIds.has(ref.catalogId)
        );

        campaignObject.leaderboards = [...uniqueLeaderboardIds];

        return {
          outcomeDefinitionSlug: outcomeDefinition?.slug,
          outcomeData: outcomeData,
          outcomeRules: outcomeRules?.map(({ ruleDefinition, ruleData }) => ({
            ruleDefinitionSlug: ruleDefinition?.slug,
            ruleData,
          })),
        };
      });
    };

    const activityPayload = {
      activityDefinitionSlug: activityDefinition.slug,
      name,
      description,
      activityRules: mapActivityRules(activityRules),
      activityData: activityData || {},
      outcomes: mapOutcomes(outcomes),
    };

    CUSTOM_HANDLED_RULE_SLUGS.forEach((slug) => {
      const matchingRules = activityPayload.activityRules.filter(
        (rule) => rule.ruleDefinitionSlug === slug
      );
      if (matchingRules.length > 0 && ruleStrategies[slug]) {
        ruleStrategies[slug](matchingRules);
      }
    });

    //sort the rules after custom handling to ensure proper order
    activityPayload.activityRules = sortActivityRules(
      activityPayload.activityRules,
      activityDefinition
    );

    const hasActivitiesConfigurationOutcomes = campaignOutcomes.some((outcome) =>
      ACTIVITIES_CONFIGURATION_OUTCOMES.includes(
        outcome.slug || outcome.outcomeDefinition?.slug
      )
    );
    if ((params.campaignId || hasActivitiesConfigurationOutcomes) && id) {
      activityPayload.id = id;
    }

    return activityPayload;
  });

export const updateActivitiesConfigurationOutcomes = (loaddata, originalData) => {
  const hasActivitiesConfigurationOutcomes = loaddata.outcomes.some((outcome) =>
    ACTIVITIES_CONFIGURATION_OUTCOMES.includes(outcome.outcomeDefinition?.slug)
  );

  if (hasActivitiesConfigurationOutcomes) {
    const activityIdMap = {};
    loaddata.activities.forEach((newActivity, index) => {
      const originalActivity = originalData.activities[index];
      if (originalActivity && originalActivity.id) {
        activityIdMap[originalActivity.id] = newActivity.id;
      }
    });

    loaddata.outcomes = loaddata.outcomes.map((outcome) => {
      if (
        ACTIVITIES_CONFIGURATION_OUTCOMES.includes(
          outcome.outcomeDefinition?.slug
        ) &&
        Array.isArray(outcome.outcomeData?.activities_configuration)
      ) {
        const updatedActivitiesConfiguration =
          outcome.outcomeData.activities_configuration.map((activityConfig) => {
            if (activityConfig.activity_id) {
              const newActivityId = activityIdMap[activityConfig.activity_id];
              if (newActivityId) {
                return {
                  ...activityConfig,
                  activity_id: newActivityId,
                };
              }
            }
            return activityConfig;
          });

        return {
          ...outcome,
          outcomeData: {
            ...outcome.outcomeData,
            activities_configuration: updatedActivitiesConfiguration,
          },
        };
      }
      return outcome;
    });
  }
};

export const getActivityName = (slug) => {
  const activities = [
    { slug: "birthday", displayText: "Birthday" },
    { slug: "bonus", displayText: "Bonus" },
    { slug: "check-in", displayText: "Check In" },
    { slug: "claim-reward", displayText: "Claim Reward" },
    { slug: "click-link", displayText: "Click Link" },
    { slug: "code-scan", displayText: "Code Scan" },
    { slug: "code-scan-generic", displayText: "Code Scan Generic" },
    { slug: "complete-profile", displayText: "Complete Profile" },
    { slug: "deeplink-action", displayText: "Deeplink Action" },
    { slug: "demo-purchase", displayText: "Demo Purchase" },
    { slug: "generic", displayText: "Generic" },
    { slug: "gift-processing", displayText: "Gift Processing" },
    { slug: "login", displayText: "Login" },
    { slug: "notifications-opt-in", displayText: "Notifications Opt In" },
    { slug: "open-app", displayText: "Open App" },
    { slug: "package-scan", displayText: "Package Scan" },
    { slug: "profile-completion", displayText: "Profile Completion" },
    { slug: "read-article", displayText: "Read an Article" },
    { slug: "receipt-scan", displayText: "Receipt Scan" },
    { slug: "refer-a-friend", displayText: "Refer a Friend" },
    { slug: "registration", displayText: "Registration" },
    { slug: "share-location", displayText: "Share Location" },
    { slug: "spin-the-wheel", displayText: "Spin the Wheel" },
    { slug: "streak", displayText: "Streak" },
    { slug: "support", displayText: "Support" },
    { slug: "survey", displayText: "Take a Survey" },
    { slug: "sweepstakes", displayText: "Sweepstakes" },
    { slug: "watch-video", displayText: "Watch Video" },
    { slug: "watch-a-video", displayText: "Watch Video" },
    { slug: "code-scan-generic", displayText: "Code Scan Generic" },
    {
      slug: "receipt-scan-complete-points",
      displayText: "Scan a Receipt Complete Points",
    },
    { slug: "code-scan-upload-points", displayText: "Scan a Code (Pre Generated)" },
    { slug: "watch-a-video", displayText: "Watch a Video" },
    { slug: "take-a-survey-mx", displayText: "Take a Survey" },
    { slug: "package-scan-points", displayText: "Scan a Package" },
    {
      slug: "package-scan-no-unique-code-points",
      displayText: "Scan a Package Without Unique Code",
    },
    { slug: "registration-generic", displayText: "Registration Generic" },
    { slug: "image-submission", displayText: "Submit Image" },
    { slug: "code-scan-external", displayText: "Code Scan External)" },
  ];

  return activities.find((activity) => activity.slug === slug)?.displayText;
};
